/**
 * @file time.component.tsx
 * @path packages/ui/src/time.component.tsx
 * @description A segmented 24h Time Input (HH:MM) matching the system aesthetic.
 */

import React, { useRef, useState, useEffect } from "react";
import { cn } from "@sirbastians/utils";
import { Clock } from "lucide-react";
import { Label } from "./label.component";

interface TimeInputProps {
  value?: string; // Format "HH:mm"
  onChange?: (value: string) => void;
  label?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
}

export const TimeInput = React.forwardRef<HTMLDivElement, TimeInputProps>(
  ({ value = "00:00", onChange, label, disabled, className, error }, ref) => {
    // Split initial value
    const [hours, setHours] = useState(value.split(":")[0] || "00");
    const [minutes, setMinutes] = useState(value.split(":")[1] || "00");

    const hourRef = useRef<HTMLInputElement>(null);
    const minuteRef = useRef<HTMLInputElement>(null);

    // Sync internal state if external value changes
    useEffect(() => {
      if (value) {
        const [h, m] = value.split(":");
        setHours(h);
        setMinutes(m);
      }
    }, [value]);

    const handleChange = (type: "h" | "m", val: string) => {
      let newVal = val.replace(/\D/g, ""); // Remove non-digits

      // Limits
      if (type === "h") {
        if (parseInt(newVal) > 23) newVal = "23";
      } else {
        if (parseInt(newVal) > 59) newVal = "59";
      }

      // Update State
      if (type === "h") setHours(newVal);
      else setMinutes(newVal);

      // Trigger Parent Change
      if (onChange) {
        const hFormatted = type === "h" ? newVal.padStart(2, "0") : hours;
        const mFormatted = type === "m" ? newVal.padStart(2, "0") : minutes;
        onChange(`${hFormatted}:${mFormatted}`);
      }
    };

    const handleBlur = (type: "h" | "m") => {
      // Pad with 0 on blur (e.g., "9" -> "09")
      if (type === "h") {
        const formatted = hours.padStart(2, "0");
        setHours(formatted);
        onChange?.(`${formatted}:${minutes}`);
      } else {
        const formatted = minutes.padStart(2, "0");
        setMinutes(formatted);
        onChange?.(`${hours}:${formatted}`);
      }
    };

    return (
      <div className={cn("relative w-full group", className)} ref={ref}>
        <div
          className={cn(
            // Container Layout
            "flex items-center h-12 w-full rounded-xl border px-3 text-sm transition-all duration-200",

            // Neuromorphic Well Style
            "neuro-well bg-secondary text-secondary-foreground border-input",

            // Focus-Within Ring (Highlights entire container when editing)
            "focus-within:border-muted-foreground focus-within:ring-1 focus-within:ring-muted-foreground/20",

            // Error State
            error &&
              "border-destructive focus-within:border-destructive focus-within:ring-destructive/20",

            // Disabled State
            disabled && "opacity-50 pointer-events-none cursor-not-allowed",
          )}
        >
          {/* Icon (Visual Aid) */}
          <Clock className="mr-3 h-4 w-4 text-muted-foreground" />

          {/* Hours Input */}
          <input
            ref={hourRef}
            type="text"
            maxLength={2}
            value={hours}
            onChange={(e) => handleChange("h", e.target.value)}
            onBlur={() => handleBlur("h")}
            className="w-8 bg-transparent text-center font-mono font-medium outline-none placeholder:text-muted-foreground/50"
            placeholder="HH"
          />

          <span className="text-muted-foreground font-bold mx-1">:</span>

          {/* Minutes Input */}
          <input
            ref={minuteRef}
            type="text"
            maxLength={2}
            value={minutes}
            onChange={(e) => handleChange("m", e.target.value)}
            onBlur={() => handleBlur("m")}
            className="w-8 bg-transparent text-center font-mono font-medium outline-none placeholder:text-muted-foreground/50"
            placeholder="MM"
          />

          {/* Spacer to fill clickable area */}
          <div className="flex-1" onClick={() => hourRef.current?.focus()} />
        </div>

        {label && (
          <Label
            className={cn(
              "absolute left-4 top-1.5 scale-[65%] font-medium text-muted-foreground bg-transparent origin-[0] pointer-events-none transition-all",
              error && "text-destructive",
            )}
          >
            {label}
          </Label>
        )}
      </div>
    );
  },
);
TimeInput.displayName = "TimeInput";
